import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthUser } from "@/lib/auth";

// RequirePro guards /pro/* routes. Two-layer check:
//   1. Cached localStorage role — gives an instant decision so navigations feel snappy.
//   2. Background /auth/me revalidation — handled inside useAuthUser; if the
//      server says we're unauthenticated (401 → clearAuth), the cache flips
//      to null and we re-render into a redirect.
//
// `pending` is only true on first load when there's no cache at all; in that
// rare case we render nothing for one tick rather than mistakenly redirecting
// a returning user with a valid cookie but a freshly-cleared localStorage.
function RequirePro() {
  const { user, pending } = useAuthUser();
  const location = useLocation();
  if (pending) return null;
  if (!user) {
    // Encode the originating path into BOTH the URL (?next=) and
    // history state. URL query survives the OAuth full-page round-trip
    // (Google → backend callback → /auth) where history state would be
    // lost, so backend can also see it for cookie persistence. state
    // is kept for the password-signin path which never leaves the SPA.
    const search = `?next=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={`/auth${search}`} replace state={{ from: location.pathname }} />;
  }
  if (user.role !== "pro") return <Navigate to="/chat" replace />;
  return <Outlet />;
}

// RequireAuth gates routes that need *some* signed-in user (any role).
// Used by /account.
function RequireAuth() {
  const { user, pending } = useAuthUser();
  const location = useLocation();
  if (pending) return null;
  if (!user) {
    const search = `?next=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={`/auth${search}`} replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

import { AppShellConsumer } from "@/components/AppShellConsumer";
import { AppShellAgent } from "@/components/AppShellAgent";
import { Landing } from "@/pages/Landing";
import { Auth } from "@/pages/Auth";
import { Account } from "@/pages/Account";
import { LinkAgent } from "@/pages/LinkAgent";
import { Home } from "@/pages/Home";
import { Explore } from "@/pages/Explore";
import { Rent } from "@/pages/Rent";
import { AuthorPage } from "@/pages/AuthorPage";
// D4: agent-only routes are code-split. They depend on heavy CRM /
// agent-workspace libs that consumer users (90%+ of traffic) never
// touch. Splitting them shaves ~250 KB off the consumer bundle.
import { Contact } from "@/pages/Contact";

const Agent = lazy(() => import("@/pages/Agent").then((m) => ({ default: m.Agent })));
const ClientReport = lazy(() => import("@/pages/ClientReport").then((m) => ({ default: m.ClientReport })));
const PublicReport = lazy(() => import("@/pages/PublicReport").then((m) => ({ default: m.PublicReport })));
const Crm = lazy(() => import("@/pages/Crm").then((m) => ({ default: m.Crm })));
const SellerPitch = lazy(() => import("@/pages/SellerPitch").then((m) => ({ default: m.SellerPitch })));
// Case-centric agent surface from the Claude Design handoff. Mock-data only
// for now — backend stays unchanged. Lazy because it ships a fair amount
// of inline-styled chrome that pro users land on but consumers never see.
const AgentDashboard = lazy(() => import("@/pages/pro/agent/Dashboard").then((m) => ({ default: m.AgentDashboard })));
const CasesPortfolio = lazy(() => import("@/pages/pro/agent/CasesPortfolio").then((m) => ({ default: m.CasesPortfolio })));
const CaseDetail = lazy(() => import("@/pages/pro/agent/CaseDetail").then((m) => ({ default: m.CaseDetail })));
const AgentInboxAI = lazy(() => import("@/pages/pro/agent/AgentInboxAI").then((m) => ({ default: m.AgentInboxAI })));
// Dedicated case-centric Listings pages (PROP-219) — flat routes under
// AppShellAgent that replaced the legacy listings sub-shell surface.
// Reuse the live proagentApi data layer in case-centric chrome.
const AgentListings = lazy(() => import("@/pages/pro/agent/Listings").then((m) => ({ default: m.AgentListings })));
const AgentListingDetail = lazy(() => import("@/pages/pro/agent/ListingDetail").then((m) => ({ default: m.AgentListingDetail })));
// Dedicated case-centric inbox thread list + detail (PROP-154, per the
// PROP-218 spike) — replace the legacy ProInbox/ProThreadDetail that
// render inside the deprecated AgentWorkspaceLayout sub-shell.
const InboxThreads = lazy(() => import("@/pages/pro/agent/InboxThreads").then((m) => ({ default: m.InboxThreads })));
const AgentThreadDetail = lazy(() => import("@/pages/pro/agent/ThreadDetail").then((m) => ({ default: m.ThreadDetail })));
const AgentReports = lazy(() => import("@/pages/pro/agent/Reports").then((m) => ({ default: m.AgentReports })));
const AgentChatLeads = lazy(() => import("@/pages/pro/agent/ChatLeads").then((m) => ({ default: m.AgentChatLeads })));
// Case-centric Offers aggregator (PROP-220) — flat route under AppShellAgent.
const AgentOffers = lazy(() => import("@/pages/pro/agent/Offers").then((m) => ({ default: m.AgentOffers })));
// Case-centric Calendar (PROP-221) — dedicated flat route that replaced
// the legacy calendar sub-shell surface.
const AgentCalendar = lazy(() => import("@/pages/pro/agent/Calendar").then((m) => ({ default: m.Calendar })));
// Agent-facing article authoring (/pro/articles*) is unlinked for now —
// the components under pages/pro/agent/articles/ are kept but no longer
// routed. The buyer Explore › Newsroom, public /author + /news pages, and
// the admin editorial portal are unaffected. Re-import + re-add the routes
// below to restore self-serve authoring.
import { MarketWatch } from "@/pages/MarketWatch";
import { Workspace } from "@/pages/Workspace";

// LazyShell wraps a lazy route element with a Suspense boundary so the
// fallback covers the network round-trip without forcing every parent
// route to define its own.
function LazyShell({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}


/*
 * Three separate entry points (design v2):
 *  - Buy   — gateway sends here via /buy → existing ProProp app at /workspace.
 *            Full sidebar, dashboard, comps, calculators (the existing flow).
 *  - Rent  — gateway sends here via /rent → new chat-first surface.
 *            No sidebar, top-bar trays, AI chat is the product.
 *  - Agent — direct-link only (/pro/...). Not in the gateway; pros bookmark
 *            the URL or hit RequirePro → auth → /pro/dashboard themselves.
 *  "/" hosts the gateway itself.
 */
// Route table is exported separately from the router instance so tests
// can assert structural invariants (e.g. PROP-169: the recipient-facing
// /client-report share route must stay public and un-gated, never a
// redirect into the <RequirePro> subtree).
export const routes = [
  // Gateway — no shell wrapper, public page
  { path: "/", element: <Landing /> },

  // /buy — bridge from gateway into the existing buyer workspace.
  // We keep /buy as a stable URL so the gateway doesn't have to know
  // which inner buyer surface is "home" — when we eventually move
  // the buyer landing to /chat or /dashboard, only this redirect needs
  // updating.
  { path: "/buy", element: <Navigate to="/workspace" replace /> },

  // /rent — new rent app. Design v2 ships this as a self-contained
  // surface (no consumer shell). Step 2 wires Topbar + Welcome;
  // Steps 3–5 add the thread, trays, and backend hookup.
  { path: "/rent", element: <Rent /> },

  // /author/{slug} — public author profile (house byline + agents).
  // Self-contained, no shell; reachable from newsroom bylines.
  { path: "/author/:slug", element: <AuthorPage /> },

  // Auth flow — no shell wrapper
  { path: "/auth",   element: <Auth /> },
  { path: "/login",  element: <Auth /> },
  { path: "/signup", element: <Auth /> },

  // Buyer-side invite landing. Renders standalone (no shell) so the
  // page is light + fast to load from a shared link.
  { path: "/link/agent/:id", element: <LinkAgent /> },

  // Public recipient-facing report reader. Tokenized URL minted by
  // ShareCard. No auth — recipients (sellers / buyer clients) don't
  // have ProProp accounts. Self-contained chrome (no shell wrapper).
  { path: "/pro/share/:token", element: <LazyShell><PublicReport /></LazyShell> },

  // Recipient-facing client report (seller / buyer pitch). SendReportModal
  // / SellerPitch mint a `/client-report#data=…` link whose entire payload
  // lives in the URL hash — ClientReport reads window.location.hash itself,
  // needs no auth, and ships its own chrome. So it MUST be served from a
  // public, un-gated route. The old `<Navigate to="/pro/report">` redirect
  // was doubly broken: a bare-string Navigate drops the #data fragment, and
  // /pro/report sat behind <RequirePro>, bouncing recipients (who have no
  // ProProp account) to the auth wall / /chat. Serve ClientReport directly
  // here so the hash survives and no gate applies. (PROP-169)
  { path: "/client-report",      element: <LazyShell><ClientReport /></LazyShell> },
  // Public: someone who needs support may well be signed out, or never
  // have had an account at all.
  { path: "/contact",            element: <LazyShell><Contact /></LazyShell> },
  { path: "/client-report.html", element: <LazyShell><ClientReport /></LazyShell> },

  // Account — needs any signed-in user
  {
    element: <RequireAuth />,
    children: [
      { path: "/account", element: <Account /> },
    ],
  },

  // Consumer shell (green theme)
  {
    element: <AppShellConsumer />,
    children: [
      { path: "/explore",                     element: <Explore /> },
      { path: "/chat",                        element: <Home /> },
      { path: "/chat/:conversationId",        element: <Home /> },
      { path: "/workspace",                            element: <Workspace /> },
      { path: "/workspace/:wsId",                      element: <Workspace /> },
      { path: "/workspace/:wsId/:module",              element: <Workspace /> },
      { path: "/workspace/:wsId/:module/:propId",      element: <Workspace /> },
      // /rental and /rental-report removed in design v2 — both redirect
      // to the new /rent surface below at the top level (since /rent is
      // not under the consumer shell, those redirects can't sit here).
      { path: "/market-watch",   element: <MarketWatch /> },
    ],
  },

  // Agent shell — requires "pro" role
  {
    element: <RequirePro />,
    children: [
      {
        element: <AppShellAgent />,
        children: [
          // Case-centric surface (design handoff) — the agent launch page.
          // Wired to real backend (useCases / /agent/overview / /agent/suggestions).
          { path: "/pro/dashboard", element: <LazyShell><AgentDashboard /></LazyShell> },
          { path: "/pro/cases",     element: <LazyShell><CasesPortfolio /></LazyShell> },
          { path: "/pro/cases/:id", element: <LazyShell><CaseDetail /></LazyShell> },
          { path: "/pro/inbox",     element: <LazyShell><AgentInboxAI /></LazyShell> },
          // Dedicated Listings pages (PROP-219) — the case-centric
          // listings surface (legacy sub-shell removed in PROP-222).
          { path: "/pro/listings",     element: <LazyShell><AgentListings /></LazyShell> },
          { path: "/pro/listings/:id", element: <LazyShell><AgentListingDetail /></LazyShell> },
          // Dedicated inbox thread list + detail (PROP-154). Nested under
          // /pro/inbox/threads* so they don't clobber the AI parse pad at
          // /pro/inbox; rows deep-link via ROUTE_BUILDERS.proInboxThread.
          { path: "/pro/inbox/threads",     element: <LazyShell><InboxThreads /></LazyShell> },
          { path: "/pro/inbox/threads/:id", element: <LazyShell><AgentThreadDetail /></LazyShell> },
          { path: "/pro/reports",   element: <LazyShell><AgentReports /></LazyShell> },
          { path: "/pro/leads",     element: <LazyShell><AgentChatLeads /></LazyShell> },
          { path: "/pro/offers",    element: <LazyShell><AgentOffers /></LazyShell> },
          { path: "/pro/calendar",  element: <LazyShell><AgentCalendar /></LazyShell> },

          // Agent-facing article authoring (/pro/articles*) is unlinked for
          // now — see the note by the removed lazy imports above. The buyer
          // Newsroom, public /author + /news pages, and the admin editorial
          // portal are unaffected. Bounce any bookmarked authoring URL to the
          // dashboard (still inside the pro auth gate) instead of falling
          // through to the public landing.
          { path: "/pro/articles/*", element: <Navigate to="/pro/dashboard" replace /> },

          { path: "/pro/copilot",  element: <LazyShell><Agent /></LazyShell> },
          { path: "/pro/crm",      element: <LazyShell><Crm /></LazyShell> },
          { path: "/pro/pitch",    element: <LazyShell><SellerPitch /></LazyShell> },
        ],
      },
    ],
  },

  // Legacy redirects — keep old URLs working
  { path: "/index.html",              element: <Navigate to="/" replace /> },
  { path: "/zh.html",                 element: <Navigate to="/chat" replace /> },
  // /pro and /agent both land on the case-centric dashboard (the agent launch page).
  { path: "/pro",                     element: <Navigate to="/pro/dashboard" replace /> },
  { path: "/agent",                   element: <Navigate to="/pro/dashboard" replace /> },
  { path: "/agent.html",              element: <Navigate to="/pro/dashboard" replace /> },
  { path: "/crm",                     element: <Navigate to="/pro/crm" replace /> },
  { path: "/crm.html",                element: <Navigate to="/pro/crm" replace /> },
  { path: "/seller-pitch",            element: <Navigate to="/pro/pitch" replace /> },
  { path: "/seller-pitch.html",       element: <Navigate to="/pro/pitch" replace /> },
  { path: "/market-watch.html",       element: <Navigate to="/market-watch" replace /> },
  // Old rental surfaces (the 9-tab Rental.tsx and the RentalReport
  // long-form page) are gone in design v2 — everything funnels into
  // the new chat-first /rent app. Bookmarks and shared links land on
  // the new home automatically.
  { path: "/rental",                  element: <Navigate to="/rent" replace /> },
  { path: "/rental.html",             element: <Navigate to="/rent" replace /> },
  { path: "/rental-zh.html",          element: <Navigate to="/rent" replace /> },
  { path: "/rental-report",           element: <Navigate to="/rent" replace /> },
  { path: "/rental-report.html",      element: <Navigate to="/rent" replace /> },

  // Catch-all
  { path: "*", element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(routes);
